import images from '@assets/images/index'
import useTable from '@components/Table/useTable'
import { CancelToken } from 'axios'
import { useEffect, useState } from 'react'
import * as bi from 'react-bootstrap-icons'

import * as Api from '@suntex.fim/api-client'
import { $EntityLevel, EntityLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { Popover } from '@suntex.fim/ui'

import {
  ColumnBuilder,
  IEntityApiResponse,
  MailTo,
  TableBuilder,
  Visible
} from './Utilities'

///////////extend ColumnBuilder with this helper method
declare module './Utilities' {
  interface ColumnBuilder<TItem> {
    formatAsLocaleString(): ColumnBuilder<TItem>
  }
}

ColumnBuilder.prototype.formatAsLocaleString = function () {
  this.Cell = function (item) {
    return <>{item[this.FieldName].toLocaleString()}</>
  }

  return this
}

interface IEntityChildrenTableProps {
  entityLevel: EntityLevel
  entityId: number
  year: string
}

function AdminEntityChildrenList() {
  const { api } = useFramework()
  //do this until we can secure it by verifying that the user is an admin
  return <>Under construction</>

  function LevelTable(props: IEntityChildrenTableProps) {
    switch (props.entityLevel) {
      case EntityLevel.State:
        return StateRegionsTable(props)
      case EntityLevel.Region:
        return RegionDistrictsTable(props)
      case EntityLevel.District:
        return DistrictSchoolsTable(props)
      case EntityLevel.School:
        return SchoolTeamsTable(props)
      //case EntityLevel.Team: return TeamsTable(props)
      //case EntityLevel.Player: return PlayersTable(props)
      case EntityLevel.None:
        return <></>
      default:
        return <div>Invalid entity level {props.entityLevel}</div>
    }
  }

  function BaseTable<TResponse extends IEntityApiResponse<TItem>, TItem>(
    props: IEntityChildrenTableProps,
    getter: (
      params: { parentEntityId: number; year: string },
      cancelToken?: CancelToken
    ) => Promise<TResponse>,
    tableBuilder: TableBuilder<TItem>
    //,fetchPostProcessor?: (items: TItem[]) => void
  ) {
    const { calendar } = useFramework()
    const [data, setData] = useState<TItem[]>([])

    useEffect(() => {
      const getData = setTimeout(async () => {
        const response = await getter({
          parentEntityId: props.entityId,
          year: calendar.getCurrentSeason()
        })
        //const response = await api.regionsByState(35, FimUtils.currentFimYear(), apiVersion)

        setData(response.items || [])

        ////if we have a post processor
        //if (fetchPostProcessor) {
        //    //invoke it
        //    fetchPostProcessor?.(response.items!);
        //}
      }, 500)

      return () => clearTimeout(getData)
    }, [])

    const columns = tableBuilder.toColumnTypes()

    columns.push({
      accessorKey: 'action',
      id: 'actions',
      size: 40,
      enableSorting: false,
      header: () => <images.SettingTable />,
      cell: (info) => {
        const item = info.row.original

        //we're doing /parentLevel/id/children, so the rows of the table are at the child level
        const entityLevel = $EntityLevel.from(props.entityLevel - 1)

        const isBelowStateLevel = entityLevel < EntityLevel.State
        const isAbovePlayerLevel = entityLevel > EntityLevel.Player

        //var parentEntityLevel = props.entityLevel

        const entityLevelName = $EntityLevel.describe(entityLevel).label
        const entityId = (item as Record<string, unknown>).id as string
        //var entityName = (item as any).name as string
        const childEntityLevelName = isAbovePlayerLevel
          ? $EntityLevel.describe($EntityLevel.from(entityLevel - 1)).label
          : ''

        const content = (
          <div className="context-menu">
            {/*data-bind="visible: !isSalesRep()"*/}
            <a
              href={`/admin/${entityLevelName.toLowerCase()}/${entityId}/edit`}
            >
              <bi.PencilSquare />
              Edit this {entityLevelName.toLowerCase()}
            </a>

            <Visible isVisible={isBelowStateLevel}>
              <a href={`/${entityLevelName.toLowerCase()}/${entityId}`}>
                <bi.HouseFill />
                View admin home page for this {entityLevelName.toLowerCase()}
              </a>
            </Visible>

            <a
              href={`/admin/${entityLevelName.toLowerCase()}/${entityId}/${childEntityLevelName.toLowerCase()}s`}
            >
              <bi.ListTask />
              View {childEntityLevelName.toLowerCase()}s in this{' '}
              {entityLevelName.toLowerCase()}
            </a>
            {/*//    <a href="#" data-bind="$root.delete, visible: !isSalesRep()"><bi class="bi-file-x-fill"></bi>Delete this region</a>*/}
          </div>
        )

        return (
          <Popover
            content={content}
            trigger="click"
            placement="left"
            arrow={false}
          >
            <images.More />
          </Popover>
        )
      }
    })

    const Table = useTable<TItem>({
      data,
      columns,
      isLoading: false
    }).Table

    return <Table />
  }

  function StateRegionsTable(props: IEntityChildrenTableProps) {
    const tb = new TableBuilder<Api.Database_Procs_AdminGetRegions_Item>()

    tb.addColumn((x) => x.name).setHeaderText('Region Name')
    tb.addColumn((x) => x.bpCode).setHeaderText('BPC')
    tb.addColumn((x) => x.districtCount)
      .formatAsLocaleString()
      .setHeaderText('No. of Districts')
    tb.addColumn((x) => x.schoolCount)
      .formatAsLocaleString()
      .setHeaderText('No. of Schools')
    tb.addColumn((x) => x.teamCount)
      .formatAsLocaleString()
      .setHeaderText('No. of Teams')
    tb.addColumn((x) => x.activeTeamCount)
      .formatAsLocaleString()
      .setHeaderText('No. of Teams Activated')
    tb.addColumn((x) => x.stickers)
      .formatAsLocaleString()
      .setHeaderText('Total Stickers')
    tb.addColumn((x) => x.activePlayers)
      .formatAsLocaleString()
      .setHeaderText('Active Players')
    tb.addColumn((x) => x.numberOfLicenses)
      .formatAsLocaleString()
      .setHeaderText('Licensed Players')

    return BaseTable<
      Api.Models_Api_Admin_RegionsByState_Response,
      Api.Database_Procs_AdminGetRegions_Item
    >(props, (params, ct) => api.get_Admin_RegionsByState(params, ct), tb)
  }

  function RegionDistrictsTable(props: IEntityChildrenTableProps) {
    const tb = new TableBuilder<Api.Database_Procs_AdminGetDistricts_Item>()

    tb.addColumn((x) => x.name).setHeaderText('District Name')
    tb.addColumn((x) => x.bpcode).setHeaderText('BPC')
    tb.addColumn((x) => x.county)
    tb.addColumn((x) => x.userId).setHeaderText('District Coord. User Id')
    tb.addColumn((x) => x.password).setHeaderText('District Coord. Password')
    tb.addColumn((x) => x.schoolCount)
      .formatAsLocaleString()
      .setHeaderText('No. of Schools')
    tb.addColumn((x) => x.teamCount)
      .formatAsLocaleString()
      .setHeaderText('No. of Teams')
    tb.addColumn((x) => x.activeTeamCount)
      .formatAsLocaleString()
      .setHeaderText('No. of Teams Activated')
    tb.addColumn((x) => x.stickers)
      .formatAsLocaleString()
      .setHeaderText('Total Stickers')
    tb.addColumn((x) => x.averageStickers)
      .formatAsLocaleString()
      .setHeaderText('Stickers/Student')
    tb.addColumn((x) => x.activePlayers)
      .formatAsLocaleString()
      .setHeaderText('Active Players')
    tb.addColumn((x) => x.numberOfLicenses)
      .formatAsLocaleString()
      .setHeaderText('Licensed Players')

    return BaseTable<
      Api.Models_Api_Admin_DistrictsByRegion_Response,
      Api.Database_Procs_AdminGetDistricts_Item
    >(props, (params, ct) => api.get_Admin_DistrictsByRegion(params, ct), tb)
  }

  function DistrictSchoolsTable(props: IEntityChildrenTableProps) {
    const tb = new TableBuilder<Api.Database_Procs_AdminGetSchools_Item>()

    tb.addColumn((x) => x.name).setHeaderText('School Name')
    tb.addColumn((x) => x.bpCode).setHeaderText('BPC')
    tb.addColumn()
      .setCell((item) => (
        <MailTo
          name={item.principalName || ''}
          email={item.principalEmail || ''}
        />
      ))
      .setHeaderText('Principal')
    tb.addColumn((x) => x.userId).setHeaderText('Building User Id')
    tb.addColumn((x) => x.password).setHeaderText('Building Password')
    tb.addColumn()
      .setCell((item) => (
        <MailTo
          name={item.schoolContact || 'Principal'}
          email={item.email || ''}
        />
      ))
      .setHeaderText('Anchor')

    tb.addColumn()
      .setCell((item) => (
        <>
          {item.address1} {item.address2}
          <br />
          {item.city}, {item.state} {item.zip}
        </>
      ))
      .setHeaderText('Street Address')

    tb.addColumn((x) => x.activeTeamCount)
      .formatAsLocaleString()
      .setHeaderText('Teams Activated')
    tb.addColumn((x) => x.teamCount)
      .formatAsLocaleString()
      .setHeaderText('Teams')
    tb.addColumn((x) => x.stickers)
      .formatAsLocaleString()
      .setHeaderText('Total Stickers')
    tb.addColumn((x) => x.averageStickers)
      .formatAsLocaleString()
      .setHeaderText('Stickers/Student')
    tb.addColumn((x) => x.studentsPlaying)
      .formatAsLocaleString()
      .setHeaderText('Students Playing')
    tb.addColumn((x) => x.numberOfLicenses)
      .formatAsLocaleString()
      .setHeaderText('Licensed Players')

    return BaseTable<
      Api.Models_Api_Admin_SchoolsByDistrict_Response,
      Api.Database_Procs_AdminGetSchools_Item
    >(props, (params, ct) => api.get_Admin_SchoolsByDistrict(params, ct), tb)
  }

  function SchoolTeamsTable(props: IEntityChildrenTableProps) {
    const tb = new TableBuilder<Api.Database_Procs_AdminGetTeams_Item>()

    tb.addColumn((x) => x.teamName).setHeaderText('Team Name')
    tb.addColumn((x) => x.className).setHeaderText('Class Name')
    tb.addColumn()
      .setCell((item) => (
        <MailTo name={item.teacherName || ''} email={item.email || ''} />
      ))
      .setHeaderText('Team Leader')
    tb.addColumn((x) => x.userId).setHeaderText('Team Leader User Id')
    tb.addColumn((x) => x.password).setHeaderText('Team Leader Password')
    tb.addColumn((x) => x.grade).setCell((item) => (
      <>{item.grade.toGradeString()}</>
    ))
    tb.addColumn((x) => x.stickers)
      .formatAsLocaleString()
      .setHeaderText('Total Stickers')
    tb.addColumn((x) => x.averageStickers)
      .formatAsLocaleString()
      .setHeaderText('Stickers/Student')
    tb.addColumn((x) => x.studentsPlaying)
      .formatAsLocaleString()
      .setHeaderText('Students Playing')
    tb.addColumn((x) => x.assignedplayers)
      .formatAsLocaleString()
      .setHeaderText('Assigned Players')
    tb.addColumn((x) => x.poNumber).setHeaderText('PO Number')
    tb.addColumn((x) => x.created).setHeaderText('Created')

    return BaseTable<
      Api.Models_Api_Admin_TeamsBySchool_Response,
      Api.Database_Procs_AdminGetTeams_Item
    >(props, (params, ct) => api.get_Admin_TeamsBySchool(params, ct), tb)
  }
}

export default AdminEntityChildrenList
